import React, { useState, KeyboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface ChipInputProps {
  label: string;
  chips: string[];
  onChipsChange: (chips: string[]) => void;
  placeholder?: string;
  helperText?: string;
  maxChips?: number; // undefined = unlimited
}

export const ChipInput: React.FC<ChipInputProps> = ({
  label,
  chips,
  onChipsChange,
  placeholder = "Type and press Enter",
  helperText,
  maxChips,
}) => {
  const [inputValue, setInputValue] = useState("");
  // Ensure chips is always an array
  const safeChips = Array.isArray(chips) ? chips : [];
  const isUnlimited = maxChips === undefined;
  const canAddMore = isUnlimited || safeChips.length < maxChips;

  // Disable animations for large lists (performance guard)
  const disableAnimations = safeChips.length > 50;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      // Prevent duplicates - check case-insensitive
      const isDuplicate = safeChips.some(
        (chip) => chip.toLowerCase() === trimmedValue.toLowerCase()
      );
      if (canAddMore && !isDuplicate) {
        onChipsChange([...safeChips, trimmedValue]);
        setInputValue("");
      }
    } else if (e.key === "Backspace" && !inputValue && safeChips.length > 0) {
      onChipsChange(safeChips.slice(0, -1));
    }
  };

  const handleRemoveChip = (indexToRemove: number) => {
    // Remove by index and ensure no duplicates remain
    const newChips = safeChips.filter((_, index) => index !== indexToRemove);
    // Remove any duplicates that might exist
    const uniqueChips = Array.from(new Set(newChips));
    onChipsChange(uniqueChips);
  };

  return (
    <div className="space-y-2">
      <motion.label
        className="block text-sm font-medium text-[#2f4941]"
        whileHover={{ scale: 1.01 }}
      >
        {label}
      </motion.label>

      {/* Single Input Field with Inline Chips */}
      <div className="min-h-[42px] p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#2f4941]/30 focus-within:border-[#2f4941] transition bg-white flex flex-wrap gap-2 items-center">
        {disableAnimations ? (
          // No animations for large lists
          safeChips.map((chip, index) => (
            <div
              key={`${chip}-${index}`}
              className="inline-flex items-center gap-1 px-2 py-1 bg-[#2f4941] text-white text-xs rounded-full"
            >
              <span>{chip}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveChip(index);
                }}
                className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
              >
                <span className="material-icons" style={{ fontSize: "14px" }}>
                  close
                </span>
              </button>
            </div>
          ))
        ) : (
          <AnimatePresence mode="popLayout">
            {safeChips.map((chip, index) => (
              <motion.div
                key={chip}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="inline-flex items-center gap-1 px-2 py-1 bg-[#2f4941] text-white text-xs rounded-full"
              >
                <span>{chip}</span>
                <motion.button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveChip(index);
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                >
                  <span className="material-icons" style={{ fontSize: "14px" }}>
                    close
                  </span>
                </motion.button>
              </motion.div>
            ))}
          </AnimatePresence>
        )}

        {/* Input - always show if unlimited or under limit */}
        {canAddMore && (
          <div className="flex-1 flex items-center gap-2 min-w-[120px]">
            <input
              type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={safeChips.length === 0 ? placeholder : ""}
              className="flex-1 outline-none text-sm py-1 bg-transparent"
            />
            {inputValue.trim() && (
              <div className="flex items-center gap-1 text-xs text-gray-400 whitespace-nowrap">
                <span className="material-icons text-sm">keyboard_return</span>
                <span>Enter</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Helper Text with visual Enter key indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
          {!helperText && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-surface-ui text-gray-600 font-mono text-xs">
                Enter
              </span>
              <span>to add</span>
            </p>
          )}
        </div>
        {/* Counter - show limit only if defined */}
        <p className="text-xs text-gray-400">
          {safeChips.length} {isUnlimited ? "items" : `/ ${maxChips} items`}
        </p>
      </div>
    </div>
  );
};
